import { Client } from '@beconfident/api-lib';
import { AxiosInstance } from 'axios';
import { App } from 'vue';

import Environment from '@/environment';
import { getDefaultRequestHeaders } from '@/helpers/get-default-request-headers';
import { tracking } from '@/helpers/track-event';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';

const apiClient = new Client(Environment.API_URL);
const socketClient = new Client(Environment.API_SOCKET_URL);

function setupRequestInterceptors(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.request.use((config) => {
    const headers = config.headers ?? {};
    Object.assign(headers, getDefaultRequestHeaders());

    config.headers = headers;

    return config;
  });
}

function setupResponseInterceptors(axiosInstance: AxiosInstance) {
  axiosInstance.interceptors.response.use(undefined, async function (error) {
    const authStore = useAuthStore();
    const appStore = useAppStore();

    const status = error.response && error.response.status;
    const requestedURL =
      error.response && error.response.config && error.response.config.url;

    if (status === 401 && requestedURL !== '/app/auth/logout') {
      tracking.reset();
      await authStore.logout();
      // todo: redirect to home

      return;
    }

    if (
      status === 426 &&
      requestedURL !== '/status' &&
      !window.location.href.includes('/update')
    ) {
      await appStore.setNeedsUpdate(true);
      window.location.href = '/update';
    }

    return Promise.reject(error);
  });
}

function setupInterceptors(axiosInstance: AxiosInstance) {
  setupRequestInterceptors(axiosInstance);
  setupResponseInterceptors(axiosInstance);
}

export default function ApiClient(app: App) {
  return {
    // eslint-disable-next-line sonarjs/cognitive-complexity
    install() {
      app.config.globalProperties.$apiClient = apiClient;
      app.config.globalProperties.$socketClient = socketClient;
      app.provide('apiClient', apiClient);
      app.provide('socketClient', socketClient);

      setupInterceptors(apiClient.axios);
      setupInterceptors(socketClient.axios);
    },
  };
}

export { apiClient, socketClient };
